/**
 * Auto-draft an endoscopy report from the per-image annotations.
 *
 * Each still carries (lesionTaxonomy + imageAnnoApi): a procedure
 * (colonoscopy / EGD), an image label (anatomical landmark + a lesion
 * or explicit Normal), and region annotations (each tagged with a
 * lesion class). This module aggregates all of a study's image docs
 * into a structured draft the reader can review, edit, save and export:
 *
 *   · procedures — the distinct procedures the frames were labelled with
 *   · landmarks  — coverage checklist (every landmark for the detected
 *                  procedures, ticked when a frame is labelled with it)
 *   · findings   — each distinct abnormal lesion + the landmark(s) it
 *                  was seen at
 *   · photos     — one representative frame per distinct lesion
 *
 * It is an aid, not a diagnosis: the reader edits the narrative and
 * signs. No AI inference — purely a deterministic roll-up of what the
 * reader already labelled.
 */
import { type StudyImageDoc, type ImageLabel } from "./imageAnnoApi";
import { type Procedure, taxaFor, taxonById } from "./lesionTaxonomy";

export interface AutoFinding {
  key:       string;       // lesion_id || lesion_name (dedupe key)
  name:      string;       // display, incl. grade if set
  locations: string[];     // distinct landmark names where it was seen
  count:     number;       // frames it appeared on
}
export interface AutoPhoto {
  instance_uid: string;
  name:         string;    // lesion display
  landmark:     string;    // location label ("" if none)
}
export interface LandmarkCover { id: string; name: string; ticked: boolean }

export interface AutoDraft {
  generated_at: string;
  procedures:   Procedure[];
  landmarks:    LandmarkCover[];
  findings:     AutoFinding[];
  photos:       AutoPhoto[];
}

/** A lesion descriptor pulled from an image label or a region annotation. */
interface LesionRef { lesion_id?: string; lesion_name?: string; grade_id?: string }

function lesionKey(r: LesionRef): string | null {
  if (r.lesion_id) return r.lesion_id;
  const n = (r.lesion_name ?? "").trim();
  return n ? `custom:${n.toLowerCase()}` : null;
}
function lesionDisplay(r: LesionRef): string {
  const tx = taxonById(r.lesion_id);
  const base = tx?.name || (r.lesion_name ?? "").trim() || "Lesion";
  const g = r.grade_id ? tx?.grades?.find((x) => x.id === r.grade_id)?.name : undefined;
  return g ? `${base} — ${g}` : base;
}
function landmarkName(l: ImageLabel | undefined): string {
  if (!l) return "";
  return l.landmark_name || (l.landmark_id ? taxonById(l.landmark_id)?.name ?? "" : "");
}
/** A label counts as an abnormal finding when it isn't the explicit
 *  "Normal" and actually names a lesion. */
function labelLesion(l: ImageLabel | undefined): LesionRef | null {
  if (!l || l.normal) return null;
  if (!l.lesion_id && !(l.lesion_name ?? "").trim()) return null;
  return { lesion_id: l.lesion_id, lesion_name: l.lesion_name, grade_id: l.grade_id };
}

export function buildAutoDraft(docs: StudyImageDoc[]): AutoDraft {
  const procSet = new Set<Procedure>();
  const seenLandmarkIds = new Set<string>();
  const findings = new Map<string, AutoFinding>();
  const photos = new Map<string, AutoPhoto>();   // keyed by lesion key → one representative

  for (const d of docs) {
    if (d.procedure === "egd" || d.procedure === "colonoscopy") procSet.add(d.procedure);
    const lm = landmarkName(d.image_label);
    if (d.image_label?.landmark_id) seenLandmarkIds.add(d.image_label.landmark_id);

    // Collect every lesion mentioned on this frame: the whole-image
    // label plus each region annotation's class.
    const refs: LesionRef[] = [];
    const il = labelLesion(d.image_label);
    if (il) refs.push(il);
    for (const a of d.annotations ?? []) {
      const r: LesionRef = { lesion_id: a?.lesion_id, lesion_name: a?.lesion_name, grade_id: a?.grade_id };
      if (lesionKey(r)) refs.push(r);
    }

    for (const r of refs) {
      const key = lesionKey(r); if (!key) continue;
      const name = lesionDisplay(r);
      const f = findings.get(key);
      if (f) { f.count++; if (lm && !f.locations.includes(lm)) f.locations.push(lm); }
      else findings.set(key, { key, name, locations: lm ? [lm] : [], count: 1 });
      // First frame carrying this lesion becomes its representative photo.
      if (!photos.has(key)) photos.set(key, { instance_uid: d.instance_uid, name, landmark: lm });
    }
  }

  const procedures = [...procSet];
  // Landmark coverage: every landmark for the detected procedure(s)
  // (dedupe by id, keep anatomical order), ticked when it was labelled.
  const landmarks: LandmarkCover[] = [];
  const seenIds = new Set<string>();
  const procsForCoverage = procedures.length ? procedures : (["egd", "colonoscopy"] as Procedure[]);
  for (const proc of procsForCoverage) {
    for (const t of taxaFor(proc, "landmark")) {
      if (seenIds.has(t.id)) continue;
      seenIds.add(t.id);
      landmarks.push({ id: t.id, name: t.name, ticked: seenLandmarkIds.has(t.id) });
    }
  }

  return {
    generated_at: new Date().toISOString(),
    procedures,
    landmarks,
    findings: [...findings.values()],
    photos: [...photos.values()],
  };
}

const PROC_LABEL: Record<Procedure, string> = {
  egd: "Esophagogastroduodenoscopy (EGD)",
  colonoscopy: "Colonoscopy",
};

/** Readable narrative for the editable report textareas. The
 *  structured checklist + photo grid live in the draft itself (preview
 *  + PDF); these two sections give the clinician an editable starting
 *  point. */
export function autoDraftNarrative(d: AutoDraft): { procedureNotes: string; findings: string } {
  const proc = d.procedures.length
    ? d.procedures.map((x) => `• ${PROC_LABEL[x]}`).join("\n")
    : "• (procedure not labelled)";
  const seen = d.landmarks.filter((l) => l.ticked).map((l) => l.name);
  const procedureNotes =
    `Procedure(s):\n${proc}` +
    (seen.length ? `\n\nLandmarks visualised: ${seen.join(", ")}.` : "");
  const findings = d.findings.length
    ? d.findings.map((f) => `• ${f.name}${f.locations.length ? ` (${f.locations.join(", ")})` : ""}`).join("\n")
    : "• No abnormality detected — normal study.";
  return { procedureNotes, findings };
}
